import React, { FunctionComponent, useEffect, useState, useMemo } from 'react';
import {
  Box,
  Button,
  PaymentMethod,
  withStyles,
  formattedCurrency,
  SupportedCurrencies,
  getContrastColor,
  InteracWordmark,
  allCardBrands,
  AcceptedCardBrand,
  BankingPaymentRail,
  Icon,
  BankLogo,
} from 'vopay';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { usePaylink, Flow } from '@/providers/Paylink.provider';
import { useToken } from '@/providers/Token.provider';
import { useLayout } from '@/providers/Layout.provider';
import useFetchElinxPaymentMethods from '@/hooks/useApi/useFetchElinxPaymentMethods';
import styles from './PaymentMethod.module.scss';
import useInteracAutoDeposit from '@/hooks/useApi/useInteracAutoDeposit';
import { useDataFetchIndicator } from '@/hooks';
import PaymentMethodOption, { getBankRailOptionsForPaymentMethod } from './PaymentMethodOption';
import { useConvenienceFeesProvider } from '@/providers/ConvenienceFees.provider';
import { AcceptedCardBrandsTooltip } from 'UI';
import { paymentMethodToElinxConnectionMethodMap } from '@/utils';
import { useBank } from '@/providers/Bank.provider';

const cn = withStyles(styles);

const PaymentMethodContainer: FunctionComponent = () => {
  const history = useHistory();
  const { t } = useTranslation('translations', { keyPrefix: 'PaymentMethod' });
  const { color } = useLayout();
  const { token } = useToken();
  const { paylink, flow, Token } = usePaylink();
  const { setBankAccountRail } = useBank();
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<PaymentMethod>();
  const [selectedBankRails, setSelectedBankRails] = useState<Record<string, BankingPaymentRail>>({});
  const [elinxPaymentMethods, fetchElinxPaymentMethods] = useFetchElinxPaymentMethods({
    bearer: token?.JWT || '',
  });
  const [, setLoading] = useDataFetchIndicator();
  // using the 'loading' prop causes a severe lag, for some reason
  const { fetchAutoDepositStatus } = useInteracAutoDeposit();

  useEffect(() => {
    if (paylink.ClientAccountID !== '') {
      fetchElinxPaymentMethods({
        SingleUseToken: Token,
        Currency: paylink.Currency as SupportedCurrencies,
      });
    }
  }, [paylink.ClientAccountID]);

  // Initialize bank rails with first available option for each bank account
  useEffect(() => {
    if (!elinxPaymentMethods) return;

    const initialRails: Record<string, BankingPaymentRail> = {};

    elinxPaymentMethods.forEach((method) => {
      if (method.PaymentMethod === 'BankAccount' && method.ExtraFields.PaymentRails) {
        // Get filtered rail options based on paylink's allowed rails
        const availableRailOptions = getBankRailOptionsForPaymentMethod(method, paylink.BankAccountRail);

        if (availableRailOptions.length > 0) {
          // Set first available rail as default
          initialRails[method.Token] = availableRailOptions[0].rail;
        }
      }
    });

    setSelectedBankRails(initialRails);
  }, [elinxPaymentMethods, paylink.BankAccountRail]);

  useEffect(() => {
    if (!elinxPaymentMethods || elinxPaymentMethods.length !== 0 || paylink.PaymentMethodDisplayOptions !== 'Both')
      return;
    if (token?.PaymentSelectionMethod === 'bank') {
      history.replace('/connect/bank');
    } else if (token?.PaymentSelectionMethod === 'credit') {
      history.replace({
        pathname: '/connect/processor/credit',
        state: { origin: 'token' },
      });
    } else if (token?.PaymentSelectionMethod === 'debitcard') {
      history.replace({
        pathname: '/connect/processor/debitcard',
        state: { origin: 'token' },
      });
    } else if (token?.PaymentSelectionMethod === 'email') {
      history.replace({
        pathname: '/connect/processor/email',
        state: { origin: 'token' },
      });
    } else {
      history.replace({
        pathname: '/connect',
        state: { paymentMethod: token?.PaymentSelectionMethod, origin: 'token' },
      });
    }
  }, [elinxPaymentMethods]);

  // Helper function to check if all card brands are accepted
  const isAllCardBrandsAccepted = useMemo(() => {
    if (!token?.AcceptedCardBrands || token.AcceptedCardBrands.length === 0) return true;
    if (token.AcceptedCardBrands.length === allCardBrands.length) return true;
    return false;
  }, [token?.AcceptedCardBrands]);

  // Helper function to check if a card brand is accepted
  const isCardBrandAccepted = (cardBrand: string): boolean => {
    if (isAllCardBrandsAccepted) return true;
    if (!token?.AcceptedCardBrands) return true;
    const normalizedBrand = cardBrand.toLowerCase();
    return token.AcceptedCardBrands.some((brand: AcceptedCardBrand) => brand.toLowerCase() === normalizedBrand);
  };

  // Check if we should show combined credit/debit card section
  const shouldShowCombinedCard = useMemo(() => {
    return token?.CardTypeValidation === false;
  }, [token?.CardTypeValidation]);

  const groupedPaymentMethods = useMemo(() => {
    if (!elinxPaymentMethods) return [];

    let filteredMethods =
      flow === Flow.WITHDRAW__MULTIPLE_PAYMENTS || flow === Flow.FUND__SCHEDULED
        ? elinxPaymentMethods.filter((method) => method.PaymentMethod === 'BankAccount')
        : flow === Flow.FUND__SINGLE
          ? elinxPaymentMethods.filter((method) => method.PaymentMethod !== 'PhoneNumber')
          : elinxPaymentMethods;

    // Filter out cards that don't match accepted brands or card type validation
    filteredMethods = filteredMethods.filter((method) => {
      // Only filter card payment methods
      if (method.PaymentMethod === 'CreditCard' || method.PaymentMethod === 'DebitCard') {
        // Check accepted card brands
        const cardBrand = method.ExtraFields.CardBrand;
        if (!isCardBrandAccepted(cardBrand || '')) {
          return false;
        }

        // If CardTypeValidation is enabled, filter by payment selection method
        if (token?.CardTypeValidation === true) {
          const paymentSelectionMethod = token?.PaymentSelectionMethod || '';

          // Check if credit cards are enabled
          const isCreditEnabled = paymentSelectionMethod.includes('credit');
          // Check if debit cards are enabled
          const isDebitEnabled = paymentSelectionMethod.includes('debitcard');

          // Filter out credit cards if not enabled
          if (method.PaymentMethod === 'CreditCard' && !isCreditEnabled) {
            return false;
          }

          // Filter out debit cards if not enabled
          if (method.PaymentMethod === 'DebitCard' && !isDebitEnabled) {
            return false;
          }
        }

        return true;
      }
      // Keep all non-card payment methods
      return true;
    });
    // Filter based on PaymentSelectionMethod
    if (token?.PaymentSelectionMethod) {
      const allowedMethods = token.PaymentSelectionMethod.split(',');

      filteredMethods = filteredMethods.filter((method) => {
        const methodType = method.PaymentMethod.toLowerCase();

        // Map payment methods to selection method values
        if (allowedMethods.includes('bank') && methodType === 'bankaccount') return true;
        if (allowedMethods.includes('email') && (methodType === 'email' || methodType === 'phonenumber')) return true;
        if (allowedMethods.includes('credit') && methodType === 'creditcard') return true;
        if (
          (allowedMethods.includes('debitcard') || (allowedMethods.includes('credit') && !token?.CardTypeValidation)) &&
          methodType === 'debitcard'
        )
          return true;
        if (allowedMethods.includes('googlepay') && methodType === 'googlepay') return true;

        return false;
      });
    }

    // Filter bank accounts by allowed rails from paylink
    if (paylink.BankAccountRail && paylink.BankAccountRail.length > 0) {
      filteredMethods = filteredMethods.filter((method) => {
        // Only filter bank accounts
        if (method.PaymentMethod !== 'BankAccount') return true;

        // Get available rails for this bank account
        const availableRails = getBankRailOptionsForPaymentMethod(method, paylink.BankAccountRail);

        // Only include if there are available rails
        return availableRails.length > 0;
      });
    }

    if (filteredMethods.length >= 1) {
      return filteredMethods.reduce(
        (acc, method) => {
          let paymentMethod: string;

          if (method.PaymentMethod === 'BankAccount') {
            paymentMethod = 'BankAccount';
          } else if (method.PaymentMethod === 'Email' || method.PaymentMethod === 'PhoneNumber') {
            paymentMethod = 'Email';
          } else if (method.PaymentMethod === 'CreditCard') {
            paymentMethod = shouldShowCombinedCard ? 'Card' : 'CreditCard';
          } else if (method.PaymentMethod === 'DebitCard') {
            paymentMethod = shouldShowCombinedCard ? 'Card' : 'DebitCard';
          } else {
            return acc;
          }

          if (!acc[paymentMethod]) {
            acc[paymentMethod] = [];
          }
          acc[paymentMethod].push(method);
          return acc;
        },
        {} as { [key: string]: PaymentMethod[] },
      );
    }
    return {};
  }, [elinxPaymentMethods, flow, isAllCardBrandsAccepted, isCardBrandAccepted, shouldShowCombinedCard]);

  const { getProcessingFee } = useConvenienceFeesProvider();

  // Unified selection value for all radio buttons
  const selectedValue = useMemo(() => {
    if (!selectedPaymentMethod) return '';

    // For bank accounts with multiple rails, include the rail in the value
    if (selectedPaymentMethod.PaymentMethod === 'BankAccount') {
      const bankRailOptions = getBankRailOptionsForPaymentMethod(selectedPaymentMethod, paylink.BankAccountRail);
      const hasMultipleRails = bankRailOptions.length > 1;

      if (hasMultipleRails && selectedBankRails[selectedPaymentMethod.Token]) {
        return `${selectedPaymentMethod.Token}-${selectedBankRails[selectedPaymentMethod.Token]}`;
      }
    }

    return selectedPaymentMethod.Token;
  }, [selectedPaymentMethod, selectedBankRails]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!elinxPaymentMethods) return;

    const value = e.target.value;

    // Check if this is a bank rail value (contains hyphen)
    if (value.includes('-')) {
      const [token, rail] = value.split('-');
      const paymentMethod = elinxPaymentMethods.find((method) => method.Token === token);

      if (paymentMethod) {
        setSelectedPaymentMethod(paymentMethod);
        setSelectedBankRails((prev) => ({
          ...prev,
          [token]: rail as BankingPaymentRail,
        }));
      }
    } else {
      // Regular payment method
      const paymentMethod = elinxPaymentMethods.find((method) => method.Token === value);
      setSelectedPaymentMethod(paymentMethod);

      // If it's a bank account with a single rail, ensure the rail is set
      if (paymentMethod && paymentMethod.PaymentMethod === 'BankAccount') {
        const availableRailOptions = getBankRailOptionsForPaymentMethod(paymentMethod, paylink.BankAccountRail);
        if (availableRailOptions.length === 1) {
          setSelectedBankRails((prev) => ({
            ...prev,
            [value]: availableRailOptions[0].rail,
          }));
        }
      }
    }
  };

  const total = useMemo(() => {
    if (!selectedPaymentMethod) return paylink.Amount;

    const selectedRail =
      selectedPaymentMethod.PaymentMethod === 'BankAccount'
        ? selectedBankRails[selectedPaymentMethod.Token]
        : undefined;

    const feeData = getProcessingFee({
      paymentMethod: paymentMethodToElinxConnectionMethodMap[selectedPaymentMethod.PaymentMethod],
      province: selectedPaymentMethod.ExtraFields.ProvinceAbbreviation || undefined,
      bankingPaymentRail: selectedRail,
    });

    return feeData?.totalAmountNumeric.toString() || paylink.Amount;
  }, [selectedPaymentMethod, selectedBankRails, token, paylink]);

  if (!elinxPaymentMethods) return null;

  return (
    <Box maxwidth="490px" grow="1" padding="16px 0 0 0">
      <div className={cn('paymentMethodWrapper')}>
        <Box
          flx="col"
          jc="center"
          ai="center"
          height="auto"
          padding="20px"
          gapSize="8px"
          borderRadius="12px"
          width="280px"
          boxShadow="6px 6px 24px 0px rgba(0, 0, 0, 0.14)"
          background="white"
        >
          <div className={cn('total')}>
            {flow <= Flow.WITHDRAW__MULTIPLE_PAYMENTS ? t('totalReceiving') : t('totalDue')}
          </div>
          <div className={cn('amount')}>
            {paylink.Currency} <span>{formattedCurrency(total)}</span>
          </div>
        </Box>
        <p className={cn('description')}>
          {flow <= Flow.WITHDRAW__MULTIPLE_PAYMENTS ? t('payDescription') : t('collectDescription')}
        </p>
        <Box
          flx="col"
          jc="center"
          ai="center"
          height="auto"
          padding="18px 20px"
          gapSize="8px"
          borderRadius="12px"
          width="100%"
          boxShadow="6px 6px 24px 0px rgba(0, 0, 0, 0.14)"
        >
          <Box flx="col" gapSize="16px" width="100%">
            <div className={cn('paymentMethodContainer', 'scrollbar')}>
              {Object.entries(groupedPaymentMethods).map(([method, paymentMethods]) => (
                <Box key={method} flx="col" pb="sm" width="100%" height="auto">
                  <div className={cn('paymentHeader')}>
                    {method === 'BankAccount' ? (
                      t('bank')
                    ) : method === 'Card' ? (
                      <Box ai="center" gapSize="4px">
                        <span>{t('creditDebitCard', { defaultValue: 'Credit/Debit Card' })}</span>
                        <AcceptedCardBrandsTooltip acceptedCardBrands={token?.AcceptedCardBrands} position="right" />
                      </Box>
                    ) : method === 'CreditCard' ? (
                      <Box ai="center" gapSize="4px">
                        <span>{t('creditCard', { defaultValue: 'Credit Card' })}</span>
                        <AcceptedCardBrandsTooltip acceptedCardBrands={token?.AcceptedCardBrands} position="right" />
                      </Box>
                    ) : method === 'DebitCard' ? (
                      <Box ai="center" gapSize="4px">
                        <span>{t('debitCard', { defaultValue: 'Debit Card' })}</span>
                        <AcceptedCardBrandsTooltip acceptedCardBrands={token?.AcceptedCardBrands} position="right" />
                      </Box>
                    ) : (
                      <InteracWordmark
                        type={
                          flow === Flow.FUND__SINGLE || flow === Flow.FUND__SCHEDULED ? 'moneyRequest' : 'eTransfer'
                        }
                      />
                    )}
                  </div>
                  <Box flx="col" gapSize="6px">
                    {paymentMethods.map((data) => {
                      // Check if this bank account has multiple rails
                      const bankRailOptions = getBankRailOptionsForPaymentMethod(data, paylink.BankAccountRail);
                      const hasMultipleRails = bankRailOptions.length > 1;

                      if (hasMultipleRails) {
                        // Render bank header + nested rail options
                        return (
                          <div key={data.Token} className={cn('bankAccountWithRailsContainer')}>
                            {/* Bank Account Header (non-interactive) */}
                            <div className={cn('bankAccountHeader')}>
                              <Box gapSize="8px" jc="fs" ai="center" width="100%">
                                <div className={cn('iconContainer')}>
                                  {data.ExtraFields.InstitutionName === 'VOPAY TESTING BANK' ? (
                                    <Icon icon="vLogo" width="24px" height="24px" />
                                  ) : (
                                    <BankLogo
                                      width="24px"
                                      height="24px"
                                      financialInstitutionNumber={data.ExtraFields.BankID || ''}
                                    />
                                  )}
                                </div>
                                <div className={cn('bankHeaderDetails')}>
                                  <div className={cn('bankRow')}>
                                    <span>
                                      {data.ExtraFields.InstitutionName === '-' ? '' : data.ExtraFields.InstitutionName}
                                    </span>
                                    <span>{data.PaymentDetails?.slice(-7)}</span>
                                  </div>
                                </div>
                              </Box>
                            </div>

                            {/* Nested Rail Options as Radio Items */}
                            <div className={cn('nestedRailOptions')}>
                              {bankRailOptions.map((railOption) => {
                                const railValue = `${data.Token}-${railOption.rail}`;

                                return (
                                  <label
                                    key={railValue}
                                    className={cn('railOptionLabel', selectedValue === railValue && 'selected')}
                                  >
                                    <input
                                      type="radio"
                                      name="payment-method-selection"
                                      value={railValue}
                                      checked={selectedValue === railValue}
                                      onChange={handleChange}
                                      className={cn('hiddenRadio')}
                                    />
                                    <div className={cn('railOptionContent')}>
                                      <PaymentMethodOption
                                        data={data}
                                        isNested
                                        railOption={railOption}
                                        allowedRails={paylink.BankAccountRail}
                                      />
                                    </div>
                                    <div className={cn('radioIndicator')} />
                                  </label>
                                );
                              })}
                            </div>
                          </div>
                        );
                      }

                      // Regular payment method (non-bank or single rail bank)
                      return (
                        <label
                          key={data.Token}
                          className={cn('paymentOptionLabel', selectedValue === data.Token && 'selected')}
                        >
                          <input
                            type="radio"
                            name="payment-method-selection"
                            value={data.Token}
                            checked={selectedValue === data.Token}
                            onChange={handleChange}
                            className={cn('hiddenRadio')}
                          />
                          <div className={cn('railOptionContent')}>
                            <PaymentMethodOption
                              data={data}
                              railOption={bankRailOptions[0]}
                              allowedRails={paylink.BankAccountRail}
                            />
                          </div>
                          <div className={cn('radioIndicator')} />
                        </label>
                      );
                    })}
                  </Box>
                </Box>
              ))}
            </div>

            {paylink.PaymentMethodDisplayOptions === 'Both' && (
              <Box jc="fe" ai="fe" width="100%" height="auto" pe="sm">
                <button
                  className={cn('newPaymentButton')}
                  type="button"
                  onClick={() => {
                    if (token?.PaymentSelectionMethod === 'bank') {
                      history.push('/connect/bank');
                    } else if (token?.PaymentSelectionMethod === 'credit') {
                      history.push({
                        pathname: '/connect/processor/credit',
                        state: { origin: 'token' },
                      });
                    } else if (token?.PaymentSelectionMethod === 'debitcard') {
                      history.push({
                        pathname: '/connect/processor/debitcard',
                        state: { origin: 'token' },
                      });
                    } else if (token?.PaymentSelectionMethod === 'email') {
                      history.push({
                        pathname: '/connect/processor/email',
                        state: { origin: 'token' },
                      });
                    } else {
                      history.push({
                        pathname: '/connect',
                        state: { paymentMethod: token?.PaymentSelectionMethod, origin: 'token' },
                      });
                    }
                  }}
                >
                  {`+ ${t('paymentMethod')}`}
                </button>
              </Box>
            )}
          </Box>
        </Box>
        <div className={cn('buttonContainer')}>
          <Button
            id="continue-button"
            label={t('continueButton')}
            width="100%"
            fontWeight={700}
            round
            background={!selectedPaymentMethod ? '#dedede' : color}
            textColor={!selectedPaymentMethod ? '#626262' : getContrastColor(color)}
            isDisabled={!selectedPaymentMethod}
            onClick={async () => {
              let hasAutoDeposit = selectedPaymentMethod?.ExtraFields.AutoDepositEnabled || false;

              if (selectedPaymentMethod && selectedPaymentMethod?.PaymentMethod === 'Email') {
                try {
                  setLoading(true);
                  const autoDepositStatusResp = await fetchAutoDepositStatus(selectedPaymentMethod.PaymentDetails);
                  hasAutoDeposit = autoDepositStatusResp.AutoDepositEnabled;
                } finally {
                  setLoading(false);
                }
              }

              const shouldRedirectToEmailForm =
                (selectedPaymentMethod?.PaymentMethod === 'Email' ||
                  selectedPaymentMethod?.PaymentMethod === 'PhoneNumber') &&
                !selectedPaymentMethod.ExtraFields.HasQuestionAndAnswer &&
                !hasAutoDeposit &&
                flow === Flow.WITHDRAW__SINGLE;

              // Store selected bank rail if applicable
              if (selectedPaymentMethod?.PaymentMethod === 'BankAccount') {
                const selectedRail = selectedBankRails[selectedPaymentMethod.Token];
                if (selectedRail) {
                  setBankAccountRail(selectedRail);
                }
              }

              if (shouldRedirectToEmailForm) {
                history.push({
                  pathname: '/connect/email/form',
                  state: { type: 'edit', selectedPaymentMethod, amount: total },
                });
              } else {
                history.push({
                  pathname: '/connect/payment-method/summary',
                  state: {
                    selectedPaymentMethod,
                    token: selectedPaymentMethod?.Token,
                    amount: total,
                  },
                });
              }
            }}
          />
        </div>
      </div>
    </Box>
  );
};

export default PaymentMethodContainer;
